// Imports => Vendor
import axios from "axios";

// Imports => MOBX
import { makeObservable, observable, computed, action } from "mobx";

// Imports => Constants
import { KEYS } from "@opp/ui-kit";

// Imports => Utilities
import {
  AcSaveState,
  AcRemoveState,
  AcIsSet,
  AcIsNull,
  AcIsUndefined,
  AcFormatErrorMessage,
  AcFormatErrorCode,
} from "@opp/ui-kit";
import { AcAutoLoad, AcAutoSave } from "@utils";

const _default = {
  options: {},
  profile: {
    two_factor: true,
  },
  profiles: null,
  merchant_profiles: null,
  bank_accounts: null,
};

let app = {};

export class ProfileStore {
  constructor(store) {
    makeObservable(this);

    AcAutoSave(this);

    app.store = store;
  }

  @observable
    profile = _default.profile;

  @observable
    profiles = _default.profiles;

  @observable
    merchant_profiles = _default.merchant_profiles;

  @observable
    bank_accounts = _default.bank_accounts;

  @computed
  get current_profile() {
    return this.profile;
  }

  @computed
  get current_profile_uid() {
    return this.profile && this.profile.uid;
  }

  @computed
  get current_role() {
    return this.profile && this.profile.role;
  }

  @computed
  get current_roles() {
    return this.profile && this.profile.roles;
  }

  @computed
  get current_permissions() {
    return this.profile && this.profile.permissions;
  }

  @computed
  get current_profiles() {
    return this.profiles;
  }

  @computed
  get current_merchant_profiles() {
    return this.merchant_profiles;
  }

  @computed
  get current_bank_accounts() {
    return this.bank_accounts;
  }

  @computed
  get current_partner_currency() {
    if (
      this.merchant_profiles &&
      this.merchant_profiles.partner &&
      this.merchant_profiles.partner.currency
    ) {
      return this.merchant_profiles.partner.currency;
    } else {
      return "EUR";
    }
  }

  @observable
    loading = {
      status: false,
      message: null,
    };

  @computed
  get is_loading() {
    return this.loading.status;
  }

  @action
    setLoading = (state, message) => {
      this.loading = {
        status: state || false,
        message,
      };
    };

  @observable
    busy = {
      status: false,
      message: null,
    };

  @computed
  get is_busy() {
    return this.busy.status;
  }

  @action
    setBusy = (state, message) => {
      this.busy = {
        status: state || false,
        message,
      };
    };

  @action
    setProfile = (object) => {
      return new Promise(async (resolve) => {
        let profile = object;
        const { permissions } = profile;

        if (permissions) {
          const len = permissions.length;
          let n = 0;
          let formatted = {};

          for (n; n < len; n++) {
            const item = permissions[n];
            formatted[item.toUpperCase()] = true;
          }

          profile.permissions = formatted;
        }

        await this.set(KEYS.PROFILE, profile);
        resolve();
      });
    };

  @action
    list = (t, busy = false, isAuthorized = false) => {
      if (busy) this.setBusy(true);
      else this.setLoading(true);

      return app.store.api.profile
        .list()
        .then(async (response) => {
          await this.set(KEYS.MERCHANT_PROFILES, response);
          const { merchant } = response;
          if (AcIsSet(merchant) && AcIsSet(merchant.uid)) {
            AcSaveState(KEYS.XUSR_TOKEN, merchant.uid);
          }

          if (busy) this.setBusy(false);
          else this.setLoading(false);

          return response;
        })
        .catch((error) => {
          if (!axios.isCancel(error) && isAuthorized) {
            app.store.toasters.add({
              variant: "error",
              title: t("profile.failed-to-retrieve-merchant-accounts", {
                ns: "store",
              }),
              description: AcFormatErrorMessage(error),
              code: AcFormatErrorCode(error),
            });
          }

          if (busy) this.setBusy(false);
          else this.setLoading(false);

          if (!axios.isCancel(error) && isAuthorized) throw error;
        });
    };

  @action
    list_profiles = (t) => {
      this.setLoading(true);

      return app.store.api.profile
        .list_profiles({
          expand: ["bank_account", "virtual_ibans"],
          perpage: 200,
        })
        .then(async (response) => {
          await this.set(KEYS.PROFILES, response);

          this.setLoading(false);

          return response;
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            app.store.toasters.add({
              variant: "error",
              title: t("profile.failed-to-retrieve-profiles", { ns: "store" }),
              description: AcFormatErrorMessage(error),
              code: AcFormatErrorCode(error),
            });
          }

          this.setLoading(false);

          if (!axios.isCancel(error)) throw error;
        });
    };

  @action
    who_am_i = (t, busy = false, chained = false) => {
      if (busy) this.setBusy(true);
      else this.setLoading(true);

      return app.store.api.profile
        .who_am_i()
        .then(async (response) => {
          await this.set(KEYS.PROFILE, response);

          if (!chained) {
            if (busy) this.setBusy(false);
            else this.setLoading(false);
          }

          return response;
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            app.store.toasters.add({
              variant: "error",
              title: t("profile.failed-to-retrieve-merchant-account", {
                ns: "store",
              }),
              description: AcFormatErrorMessage(error),
              code: AcFormatErrorCode(error),
            });
          }

          if (busy) this.setBusy(false);
          else this.setLoading(false);

          if (!axios.isCancel(error)) throw error;
        });
    };

  @action
    list_bank_accounts = (t) => {
      this.setLoading(true);

      return app.store.api.bank_accounts
        .list({ perpage: 100 })
        .then(async (response) => {
          await this.set(KEYS.BANK_ACCOUNTS, response);

          this.setLoading(false);

          return response;
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            app.store.toasters.add({
              variant: "error",
              title: t("profile.failed-to-retrieve-bank-accounts", {
                ns: "store",
              }),
              description: AcFormatErrorMessage(error),
              code: AcFormatErrorCode(error),
            });
          }

          this.setLoading(false);

          if (!axios.isCancel(error)) throw error;
        });
    };

  @action
    update = (t, data) => {
      this.setBusy(true);

      return app.store.api.profile
        .update(data)
        .then(async (response) => {
          await this.set(KEYS.PROFILE, response);

          app.store.toasters.add({
            variant: "success",
            title: t("profile.saved-success", { ns: "store" }),
          });

          this.setBusy(false);
          return response;
        })
        .catch((error) => {
          this.setBusy(false);

          if (!axios.isCancel(error))
            app.store.toasters.add({
              variant: "error",
              title: t("profile.saved-failed", { ns: "store" }),
              description: AcFormatErrorMessage(error),
            });

          if (!axios.isCancel(error)) throw error;
        });
    };

  @action
    set = (target, value, save = false) => {
      if (!AcIsSet(target)) return;
      if (AcIsUndefined(this[target])) return;
      if (AcIsUndefined(value)) return;

      return new Promise((resolve) => {
        this[target] = value;
        if (save) AcSaveState(target, value);
        resolve();
      });
    };

  @action
    setState = (target, property, value, save = false) => {
      if (!AcIsSet(target)) return;
      if (AcIsUndefined(this[target])) return;
      if (!AcIsSet(property)) return;
      if (AcIsUndefined(value)) return;

      this[target][property] = value;
      if (save) AcSaveState(target, value);
    };

  @action
    reset = (target, save = false) => {
      if (!AcIsSet(target)) return;
      if (AcIsUndefined(this[target])) return;

      return new Promise((resolve) => {
        this[target] = _default[target];
        if (save && AcIsNull(_default[target])) {
          AcRemoveState(target);
        } else if (save) {
          AcSaveState(target, _default[target]);
        }

        resolve();
      });
    };
}

export default ProfileStore;
